import React, { useState, useRef, useEffect } from 'react';
import {
    Typography,
    TextField,
    Button,
    Box,
    ButtonBase,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Paper,
    Tooltip,
    makeStyles,
    useTheme,
    useMediaQuery
} from '@material-ui/core';
import { 
    CloudUpload, 
    Delete, 
    InsertDriveFile
} from '@material-ui/icons';
import { useStateValue } from '../../context/state/provider';
import { WIZARD_STEPS } from '../../constants/wizardSteps.constants';
import { generateAgentName } from '../../utils/ordinals';

const useStyles = makeStyles((theme) => ({
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
        minHeight: 400,
        width: '100%',
        maxWidth: '100%',
        alignItems: 'center',
        justifyContent: 'center'
    },
    formContainer: {
        background: '#ffffff',
        borderRadius: theme.spacing(1),
        minHeight: 'fit-content',
        minWidth: 'fit-content',
        textAlign: 'center',
        boxShadow: '0px 5px 37px 0px rgba(56, 66, 100, 0.086)',
        padding: theme.spacing(3),
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            gap: theme.spacing(2)
        }
    },
    sectionTitle: {
        fontSize: '16px',
        lineHeight: '20px',
        color: '#6B747F',
        margin: 0,
        textAlign: 'left',
        fontWeight: 400,
        marginBottom: theme.spacing(2)
    },
    agentNameField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.spacing(0.5),
            '& fieldset': {
                borderColor: '#E5E7EB',
                borderStyle: 'solid',
                borderWidth: '1px'
            },
            '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px'
            }
        },
        '& .MuiInputBase-input': {
            padding: theme.spacing(1.5),
            fontSize: '16px',
            '&::placeholder': {
                color: '#9CA3AF',
                opacity: 1
            }
        },
        marginBottom: theme.spacing(1),
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
            marginTop: theme.spacing(0.5)
        }
    },
    topicButtonsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        justifyContent: 'flex-start',
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'center',
            gap: theme.spacing(1)
        }
    },
    topicButton: {
        padding: theme.spacing(1.5, 3),
        border: '2px solid',
        borderRadius: 24,
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        outline: 'none',
        textTransform: 'none',
        minHeight: '40px',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 2),
            fontSize: '13px',
            minHeight: '36px'
        },
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(35, 79, 226, 0.15)',
        },
        '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 1px 4px rgba(35, 79, 226, 0.2)',
        }
    },
    topicButtonSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            borderColor: theme.palette.primary.dark,
        }
    },
    topicButtonUnselected: {
        borderColor: '#E5E7EB',
        backgroundColor: '#ffffff',
        color: '#374151',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: '#f8fafc',
        }
    },
    textAreaContainer: {
        marginTop: theme.spacing(2)
    },
    textArea: {
        width: '100%',
        minHeight: 126,
        resize: 'vertical',
        padding: theme.spacing(1.5),
        border: '1px solid #E5E7EB',
        borderRadius: theme.spacing(0.5),
        fontSize: '14px',
        fontFamily: 'inherit',
        lineHeight: '20px',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main
        },
        '&:focus': {
            outline: 'none',
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
            padding: theme.spacing(1.5) - 0.5, // Compensate for thicker border
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}20`
        },
        '&::placeholder': {
            color: '#9CA3AF'
        }
    },
    uploadSection: {
        marginTop: theme.spacing(2)
    },
    uploadArea: {
        border: `1px solid #E5E7EB`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(4),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        minHeight: '86px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            minHeight: '70px'
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.primary.main
        }
    },
    uploadAreaActive: {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.primary.dark
    },
    hiddenInput: {
        display: 'none'
    },
    fileList: {
        maxHeight: 200,
        overflow: 'auto',
        marginTop: theme.spacing(2),
        border: '1px solid #E5E7EB',
        borderRadius: theme.spacing(0.5)
    },
    supportedFormats: {
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            gap: theme.spacing(0.5)
        }
    },
    errorMessage: {
        color: '#dc2c00',
        marginTop: theme.spacing(1),
        fontSize: '14px',
        textAlign: 'left'
    },
    rejectedFiles: {
        color: '#dc2c00',
        marginTop: theme.spacing(2),
        textAlign: 'left',
        '& p': {
            margin: 0,
            marginBottom: theme.spacing(0.5)
        }
    },
    bottomSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginTop: theme.spacing(3),
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(2),
            alignItems: 'stretch'
        }
    },
    helpSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            alignItems: 'center'
        }
    },
    helpButton: {
        padding: theme.spacing(0.75, 1),
        backgroundColor: '#ffffff',
        color: theme.palette.primary.main,
        border: `2px solid ${theme.palette.primary.main}`,
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        maxWidth: '230px',
        minHeight: '35px',
        fontSize: '14px',
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.main
        },
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            width: '100%'
        }
    },
    createButton: {
        padding: theme.spacing(0.75, 2),
        color: '#FFF',
        minHeight: '35px',
        fontSize: '14px',
        fontWeight: 600,
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    customTooltip: {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.common.white,
        fontSize: '12px',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(1),
        maxWidth: 200
    },
    tooltipArrow: {
        display: 'none'
    }
}));

// Predefined topic options
const topicOptions = [
    "Retail",
    "Office",
    "Residential",
    "Human Resources",
    "IT Support",
    "Restaurant",
    "Software Product",
    "Equipment",
    "Other"
];

// Default descriptions for each topic
const topicDescriptions = {
    "Retail": "This content is for a Retail Mall and provides information for visitors and shoppers. It includes the store directory and the types of products and services each store offers and general directions to each. For restaurants it provides information about the types of food offered and some samples of menu items. It includes general information about the mall, hours, parking and general directions to amenities (bathrooms, elevators, escalators). If questions are asked about becoming a tenant, provide the user with the email address and phone number of the leasing office. If the answer to the question is not available, provide the user with the email address and phone number to customer service.",
    "Office": "This content is for a commercial office building and includes content to answer questions from tenants. User can get answers to questions about building access and visitor procedures, available amenities and services and emergency procedures. If questions are asked about becoming a tenant, provide the user with the email address and phone number of the leasing office. If the answer to the question is not available, provide the user with the email address and phone number of the property manager.",
    "Residential": "This content is the Tenant Manual for an apartment building. It provides tenants with answers specific to already being a tenant in the building. If questions are asked about becoming a tenant, provide the user with the email address and phone number of the leasing office. If the answer to the question is not available, provide the user with the email address and phone number of the property manager.",
    "Human Resources": "This content is the Human Resources Policy Documentation. It contains the employee handbook, code of conduct, and HR policies. Provide the user with a summarized answer first and ask if they want a detailed answer. Once the answer is given, request the user to confirm their question was answered.",
    "IT Support": "This content is IT Support Frequently Asked Questions (FAQ) document. It contains a knowledgebase of content to answer common IT support questions and IT policies and procedures. Provide the user with a summarized answer first and ask if they want a detailed answer. Once the answer is given, request the user to confirm their question was answered.",
    "Restaurant": "This content is for a Pizza Restaurant menu. It is to allow a user to create a pizza order. Start by offering to tell the user about any current offers. Once the user has made their order, suggest some upsell items such salad, dessert and beverages.",
    "Software Product": "This content is marketing and product information for a software product to support sales. It includes details about the product features, benefits, use cases, customer stories, technical specifications, pricing and subscription plans and free trials. Throughout the conversation prompt the user to request a demo or sign up for a free test account. If the answer to the question is not available, provide the user with the email address and phone number of the sales department.",
    "Equipment": "This content is equipment documentation for a product. It includes key features, specifications, capabilities, and intended use cases, operating manuals, setup guides, maintenance schedules, and troubleshooting content. Warranty and claims procedures and information on contacting technical support or schedule service appointments. If the answer to the question is not available, provide the user with the email address and phone number of the technical support department.",
    "Other": ""
};

const ALLOWED_FILE_TYPES = ['application/pdf', 'text/plain'];
const ALLOWED_EXTENSIONS = ['.pdf', '.txt'];

export default function NameYourAgentStep({ checkTokenExpiration }) {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [{ wizardData }, dispatch] = useStateValue();
    
    // Form state - use smart naming for admin panel launches
    const defaultName = wizardData.chatbotName || generateAgentName(wizardData.currentAgents, wizardData.prefilledFromAdmin);
    const [chatbotName, setChatbotName] = useState(defaultName);
    const [isDefaultName, setIsDefaultName] = useState(!wizardData.chatbotName);
    const [selectedTopic, setSelectedTopic] = useState(wizardData.skillDescription || '');
    const [showCustomInput, setShowCustomInput] = useState(
        wizardData.skillDescription && !topicOptions.includes(wizardData.skillDescription)
    );
    const [customDescription, setCustomDescription] = useState(
        wizardData.skillDescription || ''
    );
    const [files, setFiles] = useState(wizardData.files || []);
    const [dragActive, setDragActive] = useState(false);
    
    // Error states
    const [nameError, setNameError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [fileError, setFileError] = useState('');
    const [filesRejected, setFilesRejected] = useState([]);
    
    const fileInputRef = useRef(null);

    // Update chatbot name when wizardData changes (for admin panel integration)
    useEffect(() => {
        // Only update if we haven't set a custom name yet and we have admin panel data
        if (isDefaultName && wizardData.prefilledFromAdmin && wizardData.currentAgents >= 0) {
            const smartName = generateAgentName(wizardData.currentAgents, wizardData.prefilledFromAdmin);
            setChatbotName(smartName);
        }
    }, [wizardData.currentAgents, wizardData.prefilledFromAdmin, isDefaultName]);

    // Validation functions
    const validateChatbotName = (name) => {
        if (!name.trim()) return 'Please enter an agent name';
        if (!name[0].match(/^[a-zA-Z0-9]/i)) return 'First character has to be a letter or number';
        if (name.length > 100) return 'Max name length is 100 characters';
        if (!name.replace(/[0-9]/g, "").replace(/[^a-zA-Z ]/g, "").replace(/\s/g, "").length) {
            return 'Only spaces, numbers, special characters and their combinations are not allowed';
        }
        return '';
    };

    const validateDescription = (description) => {
        if (!description.trim()) return 'Please enter a description';
        if (description && !description[0].match(/^[a-zA-Z]/i)) {
            return 'First character has to be a letter';
        }
        if (description && description.length > 5000) {
            return 'Description length has to be less than 5000 characters';
        }
        return '';
    };

    const validateFile = (file) => {
        if (!ALLOWED_FILE_TYPES.includes(file.type) && 
            !ALLOWED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))) {
            return 'Only PDF and TXT files are supported';
        }
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
            return 'file-size-exceeds-limit (10MB)';
        }
        if (file.name.toLowerCase().includes('%')) {
            return 'file-name-not-valid-%';
        }
        return null;
    };

    // Event handlers
    const handleNameChange = (event) => {
        const value = event.target.value;
        
        // If this is the first time user is typing and we have the default name, replace it entirely
        if (isDefaultName && value.length > 0) {
            setChatbotName(value);
            setIsDefaultName(false);
        } else {
            setChatbotName(value);
        }
        
        const error = validateChatbotName(value);
        setNameError(error);
    };

    const handleNameFocus = (event) => {
        // If it's the default name, select all text for easy replacement
        if (isDefaultName) {
            event.target.select();
        }
    };

    const handleNameKeyDown = (event) => {
        // If it's the default name and user starts typing, clear the field first
        if (isDefaultName && event.key.length === 1) {
            setChatbotName('');
            setIsDefaultName(false);
        }
    };

    const handleTopicSelection = (topic) => {
        setSelectedTopic(topic);
        setDescriptionError(''); // Clear any description errors when topic changes
        
        // Show textarea after topic selection
        setShowCustomInput(true);
        
        // Set default value based on topic selection
        if (topic === "Other") {
            setCustomDescription(''); // Empty for "Other" - only placeholder
        } else {
            setCustomDescription(topicDescriptions[topic] || ''); // Default description for specific topics
        }
    };

    const handleCustomDescriptionChange = (event) => {
        const value = event.target.value;
        setCustomDescription(value);
        const error = validateDescription(value);
        setDescriptionError(error);
    };

    const handleFiles = (fileList) => {
        const newFiles = Array.from(fileList);
        const validFiles = [];
        const rejectedFiles = [];

        // Reset rejected files
        setFilesRejected([]);

        for (const file of newFiles) {
            // Check for duplicates
            const isDuplicate = files.some(existingFile => 
                existingFile.name.toLowerCase() === file.name.toLowerCase()
            );
            
            if (isDuplicate) {
                rejectedFiles.push({
                    fileName: file.name,
                    errorType: 'file-name-already-exist'
                });
                continue;
            }

            const validationError = validateFile(file);
            if (validationError) {
                rejectedFiles.push({
                    fileName: file.name,
                    errorType: validationError
                });
            } else {
                validFiles.push(file);
            }
        }

        if (rejectedFiles.length > 0) {
            setFilesRejected(rejectedFiles);
        }

        if (validFiles.length > 0) {
            const updatedFiles = [...files, ...validFiles];
            setFiles(updatedFiles);
            setFileError('');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFiles(e.target.files);
        }
        e.target.value = '';
    };

    const removeFile = (index) => {
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
    };

    const handleNext = () => {
        let hasErrors = false;

        // Validate name
        const nameValidationError = validateChatbotName(chatbotName);
        if (nameValidationError) {
            setNameError(nameValidationError);
            hasErrors = true;
        }

        // Validate files
        if (files.length === 0) {
            setFileError('Please upload at least one file');
            hasErrors = true;
        }

        // Validate description
        const finalDescription = showCustomInput ? customDescription : selectedTopic;
        if (!finalDescription) {
            hasErrors = true;
        } else if (showCustomInput) {
            const descValidationError = validateDescription(customDescription);
            if (descValidationError) {
                setDescriptionError(descValidationError);
                hasErrors = true;
            }
        }

        if (!hasErrors) {
            // Check token expiration before proceeding (if token exists)
            if (checkTokenExpiration && !checkTokenExpiration()) {
                // Token expired, checkTokenExpiration handles the error display
                return;
            }

            // Update wizard data
            dispatch({
                type: 'UPDATE_WIZARD_DATA',
                data: { 
                    chatbotName: chatbotName.trim(),
                    skillDescription: finalDescription,
                    files
                }
            });

            // Go to next step
            dispatch({
                type: 'SET_CURRENT_STEP',
                step: WIZARD_STEPS.CONFIRMATION
            });
        }
    };

    const handleRequestHelp = () => {
        // Set previous step for proper navigation back
        dispatch({
            type: 'SET_PREVIOUS_STEP',
            step: WIZARD_STEPS.NAME_YOUR_AGENT
        });
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.WHITE_GLOVE_SERVICE
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const isFormValid = chatbotName.trim() && 
                       !nameError && 
                       (showCustomInput ? customDescription.trim() && !descriptionError : selectedTopic) && 
                       files.length > 0;

    return (
        <Box className={classes.stepContainer}>
            <Box className={classes.formContainer}>
                {/* Agent Name Section */}
                <Box>
                    <Typography className={classes.sectionTitle}>
                    What do you want to call your new Q&A Agent?
                    </Typography>
                    <TextField
                        className={classes.agentNameField}
                        placeholder={generateAgentName(wizardData.currentAgents, wizardData.prefilledFromAdmin)}
                        variant="outlined"
                        fullWidth
                        value={chatbotName}
                        onChange={handleNameChange}
                        onFocus={handleNameFocus}
                        onKeyDown={handleNameKeyDown}
                        error={!!nameError}
                        helperText={nameError}
                        inputProps={{ maxLength: 100 }}
                        autoFocus={!isMobile}
                    />
                </Box>

                {/* Topic Selection Section */}
                <Box>
                    <Typography className={classes.sectionTitle}>
                        What type of content will you be working with?
                    </Typography>
                    
                    <Box className={classes.topicButtonsContainer}>
                        {topicOptions.map((topic) => (
                            <ButtonBase
                                key={topic}
                                className={`${classes.topicButton} ${
                                    selectedTopic === topic 
                                        ? classes.topicButtonSelected 
                                        : classes.topicButtonUnselected
                                }`}
                                onClick={() => handleTopicSelection(topic)}
                            >
                                {topic}
                            </ButtonBase>
                        ))}
                    </Box>


                    {showCustomInput && (
                        <Box className={classes.textAreaContainer}>
                            <Typography variant="body2" className={classes.sectionTitle}>
                                Describe your content in a few sentences to improve your experience with the AI
                            </Typography>
                            <textarea
                                className={classes.textArea}
                                placeholder={`Sample for content for solar system: 
This document titled 'An Introduction to the Solar System' provides an overview of the Sun, planets, moons, dwarf planets, asteroids, comets, and the Kuiper Belt. It includes information on their characteristics, orbits, formation, and the laws of gravity governing the solar system. Intended for educational purposes.`}
                                value={customDescription}
                                onChange={handleCustomDescriptionChange}
                                maxLength={5000}
                            />
                            <Box style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginTop: '4px'
                            }}>
                                {descriptionError && (
                                    <Typography 
                                        variant="caption" 
                                        style={{ color: '#dc2c00', fontSize: '12px' }}
                                    >
                                        {descriptionError}
                                    </Typography>
                                )}
                                <Typography 
                                    variant="caption" 
                                    style={{ 
                                        color: customDescription.length > 4500 ? '#dc2c00' : '#6B747F',
                                        fontSize: '12px',
                                        marginLeft: 'auto'
                                    }}
                                >
                                    {customDescription.length}/5000
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* File Upload Section */}
                <Box className={classes.uploadSection}>
                    <Typography className={classes.sectionTitle}>
                        Upload your content
                    </Typography>
                    
                    <Paper 
                        className={`${classes.uploadArea} ${dragActive ? classes.uploadAreaActive : ''}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={handleClick}
                        elevation={0}
                    >
                        <input
                            ref={fileInputRef}
                            className={classes.hiddenInput}
                            type="file"
                            multiple
                            accept=".pdf,.txt"
                            onChange={handleFileInput}
                        />
                        
                        <CloudUpload style={{ fontSize: 32, color: '#2E56F4', marginBottom: 8 }} />
                        <Typography variant="body1" style={{ fontWeight: 500 }}>
                            Choose a file or drag it here
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ fontSize: '12px' }}>
                            PDF, TXT only (Max 10MB)
                        </Typography>
                    </Paper>

                    {fileError && (
                        <Typography className={classes.errorMessage}>
                            {fileError}
                        </Typography>
                    )}

                    {filesRejected.length > 0 && (
                        <Box className={classes.rejectedFiles}>
                            <Typography variant="body2">
                                {filesRejected.length === 1
                                    ? 'The following file has been rejected:'
                                    : 'The following files have been rejected:'}
                            </Typography>
                            {filesRejected.map((file, i) => (
                                <Typography key={`reject${i}`} variant="body2">
                                    {file.fileName.length > 15
                                        ? `${file.fileName.substring(0, 15)}...`
                                        : file.fileName}{' '}
                                    <strong>{file.errorType}</strong>
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {files.length > 0 && (
                        <Paper className={classes.fileList} elevation={0}>
                            <List dense>
                                {files.map((file, index) => (
                                    <ListItem key={index}>
                                        <InsertDriveFile style={{ marginRight: 16, color: '#6B747F' }} />
                                        <ListItemText
                                            primary={file.name}
                                            secondary={formatFileSize(file.size)}
                                            primaryTypographyProps={{ style: { fontSize: '14px' } }}
                                            secondaryTypographyProps={{ style: { fontSize: '12px' } }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton 
                                                onClick={() => removeFile(index)}
                                                size="small"
                                                style={{ color: '#dc2c00' }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    )}
                </Box>

                {/* Bottom Section */}
                <Box className={classes.bottomSection}>
                    <Box className={classes.helpSection}>
                        <Tooltip 
                            title="Click here if you need support"
                            classes={{ tooltip: classes.customTooltip, arrow: classes.tooltipArrow }}
                            placement="top"
                        >
                            <Button
                                className={classes.helpButton}
                                onClick={handleRequestHelp}
                                variant="outlined"
                            >
                                Need help?
                            </Button>
                        </Tooltip>
                    </Box>

                    <Button
                        className={classes.createButton}
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={!isFormValid}
                    >
                        Create agent
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
